/**
 * ui/postCard.tsx — shared rendering for individual social-media posts.
 *
 * Real field names confirmed against the OLD UI's proven-working
 * `PostItem.tsx` (src/container/searcher/details/items/PostItem.tsx) and the
 * typed `PostDataItem` interface in store/searcher/reducer.ts: a post row is
 * tagged `type: "PostItem"` and carries `images_url`/`videos_url` (arrays),
 * `text` (caption), `profile_id` (author handle), `timeStamp`, `like_count`
 * (what PostItem.tsx actually reads — `total_reactions_count` is the typed
 * field in PostDataItem, so both are checked here), `comments_count`,
 * `comments` (array of `{ reference_user_name, reference_content,
 * reference_user_image, timestamp }`), and `post_url`.
 *
 * Two callers share this module: PostsTemplate.tsx (the standalone "Posts
 * observer results" workflow, `type: "post"`, where every entity row IS a
 * PostItem) and SocialProfileTemplate.tsx's Posts panel (where PostItem rows
 * — if the provider ever sends them — sit as siblings next to the
 * `ProfileData` row in the same entity, matched by `from` + `profile_id`,
 * mirroring how NameCommon.tsx joins posts to a profile for combined_name).
 */
import * as React from 'react'
import { Box, IconButton, Menu, MenuItem, Typography } from '@mui/material'
import FavoriteIcon from '@mui/icons-material/Favorite'
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { radii } from '../tokens'
import { Photo, usePhotoLightbox, useTokens } from './primitives'

export type PostComment = {
  author?: string
  text?: string
  timestamp?: string
  avatar?: string
}

export type Post = {
  key: string
  from: string
  images: string[]
  videos: string[]
  text?: string
  authorId?: string
  timestamp?: string
  likeCount?: number
  commentsCount?: number
  comments: PostComment[]
  postUrl?: string
}

/** Pulls every `type: "PostItem"` row out of a flat list of entity rows.
 * `fromFallback` covers rows that don't carry their own `from` (only the
 * parent entity does). */
export function extractPostItems(rows: any[], fromFallback?: string): Post[] {
  return (Array.isArray(rows) ? rows : [])
    .filter((r) => r && typeof r === 'object' && r.type === 'PostItem')
    .map((r, idx) => ({
      key: String(r.id ?? r.post_url ?? `${r.profile_id ?? 'post'}-${idx}`),
      from: String(r.from ?? fromFallback ?? '').toLowerCase(),
      images: Array.isArray(r.images_url) ? r.images_url.filter(Boolean) : [],
      videos: Array.isArray(r.videos_url) ? r.videos_url.filter(Boolean) : [],
      text: r.text || undefined,
      authorId: r.profile_id !== undefined && r.profile_id !== null && r.profile_id !== '' ? String(r.profile_id) : undefined,
      timestamp: r.timeStamp ? String(r.timeStamp).replace('00:00:00', '').split('.')[0].trim() || undefined : undefined,
      likeCount: typeof r.like_count === 'number' ? r.like_count : typeof r.total_reactions_count === 'number' ? r.total_reactions_count : undefined,
      commentsCount: typeof r.comments_count === 'number' ? r.comments_count : undefined,
      comments: Array.isArray(r.comments)
        ? r.comments.map((c: any) => ({
            author: c?.reference_user_name || c?.author,
            text: c?.reference_content || c?.text,
            timestamp: c?.timestamp,
            avatar: c?.reference_user_image,
          }))
        : [],
      postUrl: r.post_url || undefined,
    }))
}

const miniPostBtnSx = {
  width: 22, height: 22, background: 'rgba(0,0,0,.55)', '&:hover': { background: 'rgba(0,0,0,.72)' },
} as const

/** A single post's media — image or video carousel when there's more than
 * one item, arrows in our own icon-button style (matches the story
 * carousel), click-to-enlarge wired for images via the caller's lightbox. */
function PostMedia({ images, videos, onOpenImage }: { images: string[]; videos: string[]; onOpenImage: (i: number) => void }) {
  const tk = useTokens()
  const [index, setIndex] = React.useState(0)
  const isImages = images.length > 0
  const items = isImages ? images : videos
  const total = items.length

  if (total === 0) return null

  return (
    <Box sx={{ position: 'relative', width: 118, height: 118, flex: 'none', borderRadius: `${radii.md}px`, overflow: 'hidden', background: tk.surface3 }}>
      {isImages ? (
        <Photo value={items[index]} onClick={() => onOpenImage(index)} />
      ) : (
        <Box component="video" src={items[index]} controls sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
      )}
      {total > 1 && (
        <>
          <IconButton
            size="small"
            onClick={() => setIndex((i) => (i - 1 + total) % total)}
            sx={{ position: 'absolute', left: 3, top: '50%', transform: 'translateY(-50%)', ...miniPostBtnSx }}
          >
            <ChevronLeftIcon sx={{ fontSize: 14, color: '#fff' }} />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => setIndex((i) => (i + 1) % total)}
            sx={{ position: 'absolute', right: 3, top: '50%', transform: 'translateY(-50%)', ...miniPostBtnSx }}
          >
            <ChevronRightIcon sx={{ fontSize: 14, color: '#fff' }} />
          </IconButton>
          <Box
            sx={{
              position: 'absolute', bottom: 4, left: '50%', transform: 'translateX(-50%)', fontSize: 9.5, fontWeight: 700,
              color: '#fff', background: 'rgba(0,0,0,.55)', px: '7px', py: '1px', borderRadius: '999px',
            }}
          >
            {index + 1} / {total}
          </Box>
        </>
      )}
    </Box>
  )
}

/** One post: media on the left, caption/author/date/engagement on the
 * right. Comments (if the provider sent any inline) open in a small menu
 * instead of a full popover to keep this at home inside a ResCard. */
export function PostCard({ post }: { post: Post }) {
  const tk = useTokens()
  const { openAt, lightbox } = usePhotoLightbox(post.images)
  const [commentsAnchor, setCommentsAnchor] = React.useState<HTMLElement | null>(null)

  return (
    <Box sx={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
      <PostMedia images={post.images} videos={post.videos} onOpenImage={openAt} />

      <Box sx={{ minWidth: 0, flex: 1 }}>
        {post.text && (
          <Typography sx={{ fontSize: 12.5, color: tk.text, lineHeight: 1.45, mb: '8px', wordBreak: 'break-word' }}>{post.text}</Typography>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
          <Box sx={{ minWidth: 0 }}>
            {post.authorId && <Typography sx={{ fontSize: 12, fontWeight: 700, color: tk.text }}>{post.authorId}</Typography>}
            {post.timestamp && <Typography sx={{ fontSize: 10.5, color: tk.text3 }}>{post.timestamp}</Typography>}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 'none' }}>
            {!!post.likeCount && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', color: tk.text3 }}>
                <FavoriteIcon sx={{ fontSize: 14 }} />
                <Typography sx={{ fontSize: 11 }}>{post.likeCount}</Typography>
              </Box>
            )}
            {(!!post.commentsCount || post.comments.length > 0) && (
              <Box
                component={post.comments.length > 0 ? 'button' : 'div'}
                onClick={(e: React.MouseEvent<HTMLElement>) => post.comments.length > 0 && setCommentsAnchor(e.currentTarget)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: '4px', color: tk.text3, border: 0, background: 'none', p: 0,
                  cursor: post.comments.length > 0 ? 'pointer' : 'default', fontFamily: 'inherit',
                }}
              >
                <ChatBubbleOutlineIcon sx={{ fontSize: 14 }} />
                <Typography sx={{ fontSize: 11 }}>{post.commentsCount ?? post.comments.length}</Typography>
              </Box>
            )}
            {post.postUrl && (
              <Box
                component="a"
                href={post.postUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ display: 'flex', color: tk.text3, '&:hover': { color: tk.text2 } }}
              >
                <OpenInNewIcon sx={{ fontSize: 14 }} />
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      <Menu anchorEl={commentsAnchor} open={!!commentsAnchor} onClose={() => setCommentsAnchor(null)}>
        {post.comments.slice(0, 12).map((c, i) => (
          <MenuItem key={i} disableRipple sx={{ whiteSpace: 'normal', maxWidth: 280, cursor: 'default', alignItems: 'flex-start' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px', width: '100%' }}>
              {c.author && (
                <Typography
                  component="a"
                  href={`https://instagram.com/${c.author}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  sx={{ fontSize: 11.5, fontWeight: 700, display: 'block', color: tk.text, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                >
                  {c.author}
                </Typography>
              )}
              {c.text && <Typography sx={{ fontSize: 11.5, display: 'block', color: tk.text2 }}>{c.text}</Typography>}
            </Box>
          </MenuItem>
        ))}
      </Menu>
      {lightbox}
    </Box>
  )
}

/** Posts stacked in a single column, hairline-divided — same list rhythm as
 * the rest of the redesigned result cards.
 *
 * Two ways this gets used, hence `scrollable`:
 *  - Standalone (PostsTemplate.tsx, no `fill` parent): caps itself at
 *    `maxHeight` with its own scrollbar, same reasoning as before — a real
 *    posts feed can run long and shouldn't push everything else on the page
 *    out of view.
 *  - Nested inside a `fill` ResCard (SocialProfileTemplate's Posts panel,
 *    sized to match its "Highlights and Stories" sibling column): the
 *    ResCard itself already provides the scroll container sized to that
 *    shared height, so this renders as a plain flex list instead of adding
 *    a second, independently-sized scroll area inside it.
 */
export function PostList({ posts, scrollable = true, maxHeight = 560 }: { posts: Post[]; scrollable?: boolean; maxHeight?: number }) {
  const tk = useTokens()
  if (posts.length === 0) return null
  return (
    <Box
      sx={{
        display: 'flex', flexDirection: 'column', gap: '14px',
        ...(scrollable
          ? {
              maxHeight, overflowY: 'auto', pr: '4px',
              '&::-webkit-scrollbar': { width: '6px' },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': { background: tk.border, borderRadius: '3px' },
              '&:hover::-webkit-scrollbar-thumb': { background: tk.borderStrong },
            }
          : {}),
      }}
    >
      {posts.map((p, i) => (
        <Box key={p.key} sx={{ pb: i < posts.length - 1 ? '14px' : 0, borderBottom: i < posts.length - 1 ? `1px solid ${tk.border}` : 'none' }}>
          <PostCard post={p} />
        </Box>
      ))}
    </Box>
  )
}
